import Image from 'next/image';
import useDevice from '~/hooks/use-device';
import {
  ICampaignRemainingCost,
  IFinancingOfferCampaign,
  IFinancingOfferFormatted,
} from '~/interfaces/synchrony/synchrony-interfaces';
import { splitPrice } from '~/utils/price';
import { RichText } from '~/utils/rich-text';
import styles from './synchrony-section.module.scss';
import {
  applynowBtnText,
  ccModalGetStartedText,
  promoPeriodText,
  promoPurchaseAmountText,
  WMAP_PREFIX,
  applyNow,
} from '~/modules/payment/constants';

import {
  //   TODO:: IN STORE Offers - LIMITED
  //   LimitedTimeFinancingOfferViewModel_SpecialCampaignOffer,
  Maybe,
  SynchronyConfiguration,
} from '~/gql/__generated__/types';

import { useEffect, useState } from 'react';
import { handleSynchronyApplyTealium } from '~/utils/tealium/tealium-utag-link-events';
import {
  getCampaignRemainingCost,
  getValidSpecialCampaign,
} from '~/modules/payment/synchrony/utils/financing-offer-calculation-utils';
import { calculateFinancing } from '~/modules/payment/synchrony/utils/select-financing-offer';
import Button from '~/modules/payment/synchrony/components/modal-components/button';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import ApplyNow from '~/modules/payment/synchrony/components/apply';
import LeasingModal from '~/modules/payment/leasing-modal';
import { IStoreInfo } from '~/interfaces/get-store-info';
import { getStoreInfo } from '~/services/actions/mule/get-store-info';
import SleepExpertContent from '~/components/sleep-expert/components/sleep-expert-content';
import ExtraPromoOffer from '../extra-promo-offer';
import { financingOfferLogos } from '~/modules/payment/leasing-modal/constants';
import { IPaymentState } from '~/interfaces/payment';
import { getCashbackFinanceOfferByMonths } from '~/modules/payment/synchrony/utils/get-financing-offer-details';

export interface ISynchronyModalProps {
  isOpen: boolean;
  toggleSynchronyModal: () => void;
  selectedOffer: IFinancingOfferFormatted | undefined;
  bspConfigs: SynchronyConfiguration;
  cart: ISalesOrderResponse;
  activeTab?: number;
  progressiveTotalAmount?: number;
  isExtraPromoEligible?: boolean;
  paymentState?: IPaymentState;
  enableMSChat?: boolean;
}

export const SynchronyModal: React.FC<ISynchronyModalProps> = ({
  isOpen,
  toggleSynchronyModal,
  selectedOffer,
  bspConfigs,
  cart,
  activeTab,
  progressiveTotalAmount,
  isExtraPromoEligible,
  paymentState,
  enableMSChat,
}) => {
  const [triggerFlow, setTriggerFlow] = useState<
    'applyNow' | 'prequalify' | null
  >(null);
  const [applyNowLoading, setApplyNowLoading] = useState(false);
  const [prequalifyLoading, setPrequalifyLoading] = useState(false);

  const triggerChildWindow = (flow: 'applyNow' | 'prequalify') => {
    setTriggerFlow(null);
    setTimeout(() => setTriggerFlow(flow), 0);
    if (flow === applyNow) {
      setApplyNowLoading(true);
    } else {
      setPrequalifyLoading(true);
    }
  };

  useEffect(() => {
    if (triggerFlow === null) {
      setApplyNowLoading(false);
      setPrequalifyLoading(false);
    }
  }, [triggerFlow]);
  useEffect(() => {
    if (!isOpen) {
      setTriggerFlow(null);
    }
  }, [isOpen]);

  const syfModalConfigs = bspConfigs?.buyNowPayLaterSynchrony;
  const { isSmartphone: isMobile } = useDevice();
  const [storeInfo, setStoreInfo] = useState<IStoreInfo | null>(null);
  const storeId = cart?.StoreId;
  const cashbackOffer = getCashbackFinanceOfferByMonths(
    bspConfigs?.financingOffers?.cashbackFinanceOffers,
    selectedOffer?.numOfMonths,
  );

  useEffect(() => {
    if (!isOpen || !storeId) return;
    let isMounted = true;
    (async () => {
      try {
        const info = await getStoreInfo(storeId);
        if (isMounted) setStoreInfo(info || null);
      } catch {
        if (isMounted) setStoreInfo(null);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [isOpen, storeId]);

  const modalBody = selectedOffer && (
    <section className={styles.modalBody}>
      {getHeading(
        selectedOffer?.syfModalHeading,
        selectedOffer?.syfModalSubHeading,
        selectedOffer?.additionalContentCreditCardOffer,
      )}
      {selectedOffer.isCashBack &&
        getCashBackBanner(
          syfModalConfigs?.subTitleText ?? '',
          syfModalConfigs?.cashBackPercentageHeader ?? '',
          isMobile,
        )}
      {isExtraPromoEligible && (
        <ExtraPromoOffer
          title={syfModalConfigs?.cashBackPercentageHeader ?? ''}
          subtitle={
            cashbackOffer?.cashbackBannerDetails ||
            syfModalConfigs?.subTitleText ||
            ''
          }
          imageSrc={financingOfferLogos?.[0]?.url}
          learnMoreLink={cashbackOffer?.learnMoreLink}
        />
      )}
      {getFinancingDetails(
        selectedOffer,
        syfModalConfigs?.promotionalPeriodText ?? '',
        syfModalConfigs?.promotionalPurchaseAmountText ?? '',
        false,
        cart,
        bspConfigs,
      )}
      {getDisclaimer(selectedOffer)}
      {getStarted(cart?.StoreId ?? '', storeInfo, enableMSChat)}
      <div className={styles.buttonGroup}>
        <ConsultPrequalificationLink
          preQualifyLabelText={syfModalConfigs?.preQualifyLink?.ariaLabel ?? ''}
          preQualifyLinkText={syfModalConfigs?.preQualifyLink?.linkText ?? ''}
          handlePrequalification={triggerChildWindow}
          loading={prequalifyLoading}
        />

        <ApplyNowButton
          applynowBtnText={applynowBtnText}
          applynowBtnlabel={syfModalConfigs?.applyNowLabelText ?? ''}
          handleApplyNow={triggerChildWindow}
          loading={applyNowLoading}
        />
        {triggerFlow && (
          <ApplyNow
            triggerFlow={triggerFlow}
            orderId={cart.QuotationId || cart.SalesId}
            setLoadingAction={
              triggerFlow === applyNow
                ? setApplyNowLoading
                : setPrequalifyLoading
            }
          />
        )}
      </div>
    </section>
  );

  return (
    <LeasingModal
      syfTabBody={modalBody}
      isOpen={isOpen}
      setIsOpen={toggleSynchronyModal}
      defaultOpeningTab={activeTab}
      progressiveTotalAmount={progressiveTotalAmount}
      balanceAmount={cart.balanceAmount}
      paymentState={paymentState}
    />
  );
};

const getHeading = (
  syfModalHeading: Maybe<string> | undefined,
  syfModalSubHeading: Maybe<string> | undefined,
  additionalContentCreditCardOffer: Array<string | null> | null | undefined,
): React.ReactNode => {
  return (
    <div className={styles.headerBanner}>
      <h2 tabIndex={0}>{syfModalHeading}</h2>
      <p tabIndex={0}>{syfModalSubHeading}</p>
      <ul tabIndex={0}>
        {additionalContentCreditCardOffer?.map((dataLine, index) => (
          <li key={index}>{dataLine}</li>
        ))}
      </ul>
    </div>
  );
};

const getCashBackBanner = (
  subTitleText: string,
  cashBackPercentageHeader: string,
  isMobile: boolean,
): React.ReactNode => {
  return (
    <div className={styles.cashBackBannerWrapper}>
      <div className={styles.cashBackBannerContent}>
        <div className={styles.cashBackBannerText}>
          <h1 tabIndex={0}>{cashBackPercentageHeader}</h1>
          <p className={styles.cashbackSubheading} tabIndex={0}>
            <RichText content={subTitleText} />
          </p>
        </div>
        <div className={styles.cashBackBannerImage} tabIndex={0}>
          <Image
            className="banner-image"
            src="https://images-us-prod.cms.dynamics365commerce.ms/cms/api/fknscsckgq/imageFileData/MAj8gp?pubver=1"
            alt=""
            height={isMobile ? 200 : 180}
            width={isMobile ? 143 : 242}
          />
        </div>
      </div>
    </div>
  );
};

const getStarted = (
  storeId: string | null,
  storeInfo: IStoreInfo | null,
  enableMSChat?: boolean,
): React.ReactNode => {
  return (
    <div className={styles.actionContainer}>
      <div className={styles.actionHeader}>
        <span tabIndex={0}>{ccModalGetStartedText}</span>
      </div>
      <div className={styles.sleepExpert}>
        {storeId && storeInfo && (
          <SleepExpertContent
            storeId={storeId}
            storeInfo={storeInfo}
            enableMSChat={enableMSChat}
          />
        )}
      </div>
    </div>
  );
};

const getDisclaimer = (
  financingOffer: IFinancingOfferFormatted,
): React.ReactNode => {
  return (
    financingOffer.disclaimer && (
      <div className={styles.disclaimer} tabIndex={0}>
        <RichText content={financingOffer.disclaimer} />
      </div>
    )
  );
};

interface ApplyNowButtonProps {
  applynowBtnText: string;
  applynowBtnlabel: string;
  handleApplyNow: (flow: 'applyNow' | 'prequalify') => void;
  loading?: boolean;
}

const ApplyNowButton: React.FC<ApplyNowButtonProps> = ({
  applynowBtnText,
  applynowBtnlabel,
  handleApplyNow,
  loading,
}) => {
  const applyNowClick = () => {
    handleSynchronyApplyTealium();
    handleApplyNow('applyNow');
  };
  return (
    <div
      id={`${WMAP_PREFIX}-modal-apply-btn`}
      className={styles.buttonContainer}
    >
      <Button onClick={applyNowClick} variant={'secondary'} loading={loading}>
        {applynowBtnText}
      </Button>
      <p className={styles.labelText} tabIndex={0}>
        {applynowBtnlabel}
      </p>
    </div>
  );
};

interface ConsultPrequalificationLinkProps {
  preQualifyLabelText: string;
  preQualifyLinkText: string;
  handlePrequalification: (flow: 'applyNow' | 'prequalify') => void;
  loading?: boolean;
}

const ConsultPrequalificationLink: React.FC<
  ConsultPrequalificationLinkProps
> = ({
  preQualifyLabelText,
  preQualifyLinkText,
  handlePrequalification,
  loading,
}) => {
  return (
    <div
      id={`${WMAP_PREFIX}-modal-prequalify-btn`}
      className={styles.buttonContainer}
    >
      <Button
        onClick={() => handlePrequalification('prequalify')}
        loading={loading}
      >
        {preQualifyLinkText}
      </Button>
      <p className={styles.labelText} tabIndex={0}>
        {preQualifyLabelText}
      </p>
    </div>
  );
};

const getFinancingDetails = (
  financingOffer: IFinancingOfferFormatted,
  promotionalPeriodText: string,
  promotionalPurchaseAmountText: string,
  specialFinancingEnable: boolean,
  cart: ISalesOrderResponse,
  bspConfigs: SynchronyConfiguration,
): React.ReactNode => {
  const {
    numOfMonths,
    //   TODO:: IN STORE Offers - LIMITED
    // isLTO,
    // startDate,
    // endDate,
    specialCampaign,
    everydayFinancingOfferMessage,
    //   TODO:: IN STORE Offers - LIMITED
    // limitedFinancingOfferMessage,
    cashbackFinancingOfferMessage,
  } = financingOffer;
  const isCampaignEnable = specialCampaign?.enableCampaign;
  const noOfMonthLiteral = '{numOfMonths}';
  const everydayFinancingMessage = getFormattedOfferMessage(
    everydayFinancingOfferMessage,
    noOfMonthLiteral,
    numOfMonths,
  );

  //   TODO:: IN STORE Offers - LIMITED
  //   const limitedFinancingrMessage = getFormattedOfferMessage(
  //     // limitedFinancingOfferMessage,
  //     noOfMonthLiteral,
  //     numOfMonths,
  //   );

  const cashbackFinancingrMessage = getFormattedOfferMessage(
    cashbackFinancingOfferMessage,
    noOfMonthLiteral,
    numOfMonths,
  );
  //   TODO:: IN STORE Offers - LIMITED
  // if LTO use dates otherwise use 'Everyday'
  const APR = '0%';
  const promotionalPeriod = numOfMonths;
  //   TODO:: IN STORE Offers - LIMITED
  //   const LTOeverydayOfferFlag = getLtoEverydayOfferFlag(
  //     promotionalPeriod,
  //     startDate,
  //     endDate,
  //   );

  // special campaign financing offer
  const campaign:
    | IFinancingOfferCampaign
    //   TODO:: IN STORE Offers - LIMITED
    // | LimitedTimeFinancingOfferViewModel_SpecialCampaignOffer
    | undefined = getValidSpecialCampaign(financingOffer);
  const campaignRemainingCost = campaign
    ? getCampaignRemainingCost(campaign, financingOffer, cart.balanceAmount)
    : undefined;

  const financingDescription = financingOffer.financingOfferDescription;
  const cartTotal = cart.balanceAmount;
  const { dollars, cents } = splitPrice(cartTotal);

  let totalPriceDollars = dollars;
  let totalPriceCents = cents;
  let payMonthly: number = 0;
  if (
    //   TODO:: IN STORE Offers - <LIMITED></LIMITED>
    // isLTO &&
    numOfMonths
  ) {
    const { totalAmountFinanced, monthlyPayment } = calculateFinancing(
      cartTotal,
      numOfMonths,
      campaign,
    );
    const { dollars, cents } = splitPrice(totalAmountFinanced);
    totalPriceDollars = dollars;
    totalPriceCents = cents;
    payMonthly = monthlyPayment;
  }

  const identifier = getIdentifier(isCampaignEnable, numOfMonths, bspConfigs);

  const totalPriceInCents = totalPriceCents ? `.${totalPriceCents}` : '';
  const lowCostOfferName =
    bspConfigs?.financingConfigurations?.lowCostOfferName?.toLowerCase() ?? '';
  return renderFinancingDetailsSection(
    financingOffer,
    campaign,
    campaignRemainingCost,
    //   TODO:: IN STORE Offers - LIMITED
    // limitedFinancingrMessage,
    cashbackFinancingrMessage,
    everydayFinancingMessage,
    APR,
    promotionalPeriodText,
    promotionalPeriod,
    identifier,
    promotionalPurchaseAmountText,
    totalPriceDollars,
    totalPriceInCents,
    financingDescription,
    lowCostOfferName,
    payMonthly,
    bspConfigs,
  );
};

const getIdentifier = (
  //   TODO:: IN STORE Offers - LIMITED
  //   isLTO: boolean | undefined,
  isCampaignEnable: boolean | undefined,
  month: number | null | undefined,
  bspConfigs: SynchronyConfiguration,
) => {
  //   TODO:: IN STORE Offers - LIMITED
  //   return isLTO && isCampaignEnable ? '*' : isLTO ? '††' : '***';
  if (isCampaignEnable) return '*';
  return month &&
    !bspConfigs?.synchronyFinancingPurchaseRules?.enable3PercentOffers?.includes(
      month,
    )
    ? '††'
    : '***';
};

//   TODO:: IN STORE Offers - LIMITED
// const getLtoEverydayOfferFlag = (
//   promotionalPeriod: number | undefined | null,
//   startDate: string | undefined | null,
//   endDate: string | undefined | null,
// ) => {
//   return promotionalPeriod ? 'Everyday' : `${startDate} - ${endDate}`;
// };

const getFormattedOfferMessage = (
  offerMsg: string | undefined | null,
  noOfMonthLiteral: string | undefined | null,
  noOfMonths: number | undefined | null,
) => {
  return (
    offerMsg &&
    noOfMonthLiteral &&
    offerMsg?.replace(noOfMonthLiteral, noOfMonths!.toString())
  );
};

const renderFinancingDetailsSection = (
  financingOffer: IFinancingOfferFormatted,
  campaign:
    | IFinancingOfferCampaign
    //   TODO:: IN STORE Offers - LIMITED
    // | LimitedTimeFinancingOfferViewModel_SpecialCampaignOffer
    | undefined,
  campaignRemainingCost: ICampaignRemainingCost | undefined,
  //   TODO:: IN STORE Offers - LIMITED
  //   limitedFinancingrMessage: string | undefined | null,
  cashbackFinancingrMessage: string | undefined | null,
  everydayFinancingMessage: string | undefined | null,
  APR: string,
  promotionalPeriodText: string,
  promotionalPeriod: number | undefined | null,
  identifier: string,
  promotionalPurchaseAmountText: string,
  totalPriceDollars: string,
  totalPriceInCents: string,
  financingDescription: string | null | undefined,
  lowCostOfferName: string,
  payMonthly: number,
  bspConfigs: SynchronyConfiguration,
) => {
  const {
    costPerMonth,
    isCashBack,
    //   TODO:: IN STORE Offers - LIMITED
    // isLTO,
    lowCostFinancingOfferMessage,
    financeOfferModalLabel,
  } = financingOffer;
  return (
    <>
      <div className={styles.detailsContainer}>
        {financeOfferModalLabel && (
          <div className={styles.validDates} tabIndex={0}>
            <p>{financeOfferModalLabel}</p>
          </div>
        )}

        {campaign && campaignRemainingCost ? (
          <>
            <div className={styles.detailsTop}>
              <h3 className={styles.offerBreakdown} tabIndex={0}>
                {`$${Math.ceil(campaign.initialMonthlyAmount ?? 0)}`}
                <span>/mo</span> for the first {campaign.numOfDiscountedMonths}{' '}
                months
              </h3>
            </div>
            <div className={styles.offerPayments}>
              <div className={styles.campaignMonthlyCost} aria-hidden="true">
                then{' '}
                <span>
                  {`$${Math.ceil(payMonthly)}`}
                  <span>/mo </span>
                </span>
                equal monthly payments for
                <span> {campaignRemainingCost?.months} months</span>
              </div>
            </div>
          </>
        ) : financingOffer.offerName?.toLowerCase() === lowCostOfferName ? (
          <>
            <div className={styles.lowCostheading} tabIndex={0}>
              <h3>{lowCostFinancingOfferMessage}</h3>
            </div>
          </>
        ) : (
          <>
            <div className={styles.detailsTop}>
              <h3 tabIndex={0}>
                {`$${costPerMonth}`}
                <span>/MO</span>
              </h3>
            </div>
            <div className={styles.payments} tabIndex={0}>
              {
                //   TODO:: IN STORE Offers - LIMITED
                //   isLTO ? (
                //     <span>{limitedFinancingrMessage}</span>
                //   ) :
                isCashBack ? (
                  <span>{cashbackFinancingrMessage}</span>
                ) : (
                  <span>{everydayFinancingMessage}</span>
                )
              }
            </div>
          </>
        )}

        {/* APR - PROMOTIONAL PERIOD - MIN PURCHASE - TOTAL PRICE */}
        {getFinancingOfferDetailsOnModal(
          financingOffer,
          APR,
          promotionalPeriodText,
          promotionalPeriod,
          identifier,
          promotionalPurchaseAmountText,
          totalPriceDollars,
          totalPriceInCents,
          lowCostOfferName,
          bspConfigs,
        )}
      </div>
      {/* Financing Offer Description */}
      {getFinancingDescription(financingDescription)}
    </>
  );
};

const getFinancingOfferDetailsOnModal = (
  financingOffer: IFinancingOfferFormatted,
  APR: string,
  promotionalPeriodText: string,
  promotionalPeriod: number | undefined | null,
  identifier: string,
  promotionalPurchaseAmountText: string,
  totalPriceDollars: string,
  totalPriceInCents: string,
  lowCostOfferName: string,
  bspConfigs: SynchronyConfiguration,
) => {
  return (
    <>
      {financingOffer.offerName?.toLowerCase() !== lowCostOfferName && (
        <>
          <div className={styles.offerDetails}>
            {financingOffer?.numOfMonths &&
              !bspConfigs?.synchronyFinancingPurchaseRules?.enable3PercentOffers?.includes(
                financingOffer.numOfMonths,
              ) && (
                <div className={styles.detail}>
                  <span className={styles.detailHeading}>APR</span>
                  <span className={styles.detailSubtext}>{APR}</span>
                </div>
              )}
            <div className={styles.detail}>
              <span className={styles.detailHeading}>
                {promotionalPeriodText
                  ? promotionalPeriodText
                  : promoPeriodText}
              </span>
              <span className={styles.detailSubtext}>
                {promotionalPeriod} months<sup>{identifier}</sup>
              </span>
            </div>
            <div className={styles.detail}>
              <div aria-hidden="true">
                <span className={styles.detailHeading}>
                  {
                    //   TODO:: IN STORE Offers - LIMITED
                    //   financingOffer.isLTO &&
                    promotionalPurchaseAmountText
                      ? promotionalPurchaseAmountText
                      : promoPurchaseAmountText
                  }
                </span>
                <span className={`${styles.detailSubtext} ${styles.second}`}>
                  {`$${totalPriceDollars}${totalPriceInCents}`}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
const getFinancingDescription = (
  financingDescription: string | undefined | null,
) => {
  if (financingDescription) {
    return (
      <div className={styles.offerContainer} tabIndex={0}>
        <RichText content={financingDescription} />
      </div>
    );
  }
};
